import { LiveField, WidgetInputQuickSwitch } from '@/ui'
import { CampaignFormQuestion } from '@/ui'
import { CampaignScoreValues } from '@/ui'
import { DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd'
import { CSSProperties, memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { NewOptionButton } from './buttons'
import { CONSENT_TYPE_OPTIONS, ELIGIBILITY_OPTIONS, SCORE_OPTIONS } from './constants'
import { DragHandle } from './drag-handle'

export type OptionRowProps = {
	question: CampaignFormQuestion
	option: NonNullable<CampaignFormQuestion['options']>[number]
	optionIndex: number
	backgroundColor: string
	getItemStyle: (isDragging: boolean, draggableStyle: CSSProperties) => CSSProperties
	provided: DraggableProvided
	snapshot: DraggableStateSnapshot
	handleOptionLabelChange: (questionId: string, optionId: string, label: string) => void
	handleOptionDelete: (questionId: string, optionId: string) => void
	handleOptionScoreChange: (questionId: string, optionId: string, score: CampaignScoreValues) => void
	handleOptionEligibilityChange: (questionId: string, optionId: string, isDisqualifying: boolean) => void
	handleOptionConsentTypeChange: (questionId: string, optionId: string, consentType: string) => void
	handleNewOption: (id: string, index: number) => void
}

export const OptionRow = memo(function OptionRow({
	question,
	option,
	optionIndex,
	backgroundColor,
	getItemStyle,
	provided,
	snapshot,
	handleOptionLabelChange,
	handleOptionDelete,
	handleOptionScoreChange,
	handleOptionEligibilityChange,
	handleOptionConsentTypeChange,
	handleNewOption,
}: OptionRowProps) {
	const { t } = useTranslation('editor')
	const onLabelChange = useCallback(
		(value: string) => handleOptionLabelChange(question.id, option.id, value),
		[handleOptionLabelChange, question.id, option.id],
	)

	const onDelete = useCallback(
		() => handleOptionDelete(question.id, option.id),
		[handleOptionDelete, question.id, option.id],
	)

	const onScoreChange = useCallback(
		(value: string) => {
			handleOptionScoreChange(
				question.id,
				option.id,
				(value ?? CampaignScoreValues.EXCELLENT) as CampaignScoreValues,
			)
		},
		[handleOptionScoreChange, question.id, option.id],
	)

	const onEligibilityChange = useCallback(
		(value: string) => {
			handleOptionEligibilityChange(question.id, option.id, value === 'yes')
		},
		[handleOptionEligibilityChange, question.id, option.id],
	)

	const onConsentTypeChange = useCallback(
		(value: string) => {
			handleOptionConsentTypeChange(question.id, option.id, value)
		},
		[handleOptionConsentTypeChange, question.id, option.id],
	)

	const onAddOption = useCallback(
		() => handleNewOption(question.id, optionIndex),
		[handleNewOption, question.id, optionIndex],
	)

	// For consent questions, check if this option's consent type is duplicated
	const isDuplicateConsentType = question.attribute === 'contact_consent'
		&& option.consentType
		&& (question.options || [])
			.filter((opt) => opt.id !== option.id) // Exclude current option
			.some((otherOption) => otherOption.consentType === option.consentType)
	const isNegativeConsentType = question.attribute === 'contact_consent'
		&& (option.consentType ?? 'positive') === 'negative'

	return (
		<div
			ref={provided.innerRef}
			{...provided.draggableProps}
			style={getItemStyle(snapshot.isDragging, provided.draggableProps.style ?? {})}
			className="relative group/option mb-2"
		>
			<div className="flex flex-row items-start gap-3 relative">
				<div
					{...provided.dragHandleProps}
					className="mt-0.5 ml-3 size-5 text-transparent group-hover/option:text-grey-500 flex items-center justify-center"
				>
					<DragHandle />
				</div>
				<div className="relative pt-1">
					<div
						className={`size-4 ring-2 ${question.subtype === 'multiple' ? 'rounded' : 'rounded-full'}`}
						style={{
							'--tw-ring-color': backgroundColor,
						} as CSSProperties}
					/>
				</div>
				<div className="flex-1">
					<LiveField fitSize={false} value={option.label} onChange={onLabelChange} />
				</div>
				<button
					className="text-red-500 text-sm hover:bg-red-100 px-2.5 py-1 rounded cursor-pointer opacity-0 group-hover/question:opacity-100 transition-opacity"
					onClick={onDelete}
				>
					&minus;
				</button>
			</div>

			{question.attribute === 'contact_consent'
				? (
					<div className="text-xs ml-18 mt-1">
						<div className="flex items-center gap-2">
							<WidgetInputQuickSwitch
								placeholder={t('formSimulatorLabels.consentType')}
								options={CONSENT_TYPE_OPTIONS}
								value={option.consentType ?? 'positive'}
								onChange={onConsentTypeChange}
								label={t('formSimulatorLabels.consentType')}
							/>
						</div>
						{isDuplicateConsentType && (
							<div className="text-red-600 text-xs mt-1">
								{t('formSimulatorLabels.duplicateConsentType')}
							</div>
						)}
						{isNegativeConsentType && (
							<div className="text-amber-700 text-xs mt-1 font-medium">
								{t('formSimulatorLabels.negativeConsentWarning')}
							</div>
						)}
					</div>
				)
				: (
					<div className="text-xs text-grey-500 ml-18 mt-1 flex items-center gap-2">
						<WidgetInputQuickSwitch
							placeholder={t('formSimulatorLabels.score')}
							options={SCORE_OPTIONS}
							value={question.scores?.find((score) => score.optionId === option.id)?.scoreValue
								?? CampaignScoreValues.EXCELLENT}
							onChange={onScoreChange}
							label={t('formSimulatorLabels.score')}
						/>

						<WidgetInputQuickSwitch
							placeholder={t('formSimulatorLabels.eligibility')}
							options={ELIGIBILITY_OPTIONS}
							value={(option.isDisqualifying ?? false) ? 'yes' : 'no'}
							onChange={onEligibilityChange}
							label={t('formSimulatorLabels.eligibility')}
						/>
					</div>
				)}

			<div className="ml-8 h-3 mt-2 relative">
				<NewOptionButton onClick={onAddOption} />
			</div>
		</div>
	)
})
